import { extractVideoReference } from 'services/media';

import PostDropdown from 'components/PostDropdown';
import PostBadges from 'components/PostBadges';
import RewardsButton from 'components/RewardsButton';
import { VerifiedIcon } from 'components/VerifiedIcon';
import { useAuth } from 'helpers/authContext';
import React from 'react';
import { Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { getPostImages, getReputation, numberWithCommas } from 'services/dsteem-service';
import { getTimeAgo, getTimeToExpiration } from 'services/posts-service';
const getIPFSVideoUrl = (body: string, videoField?: string) => extractVideoReference(body, { video: videoField });


const PostVideoItem = ({ post, userRole = "guest", isPlus = false, isPromoted = false, inline = false, showEmbed = false,
  canBeDeleted = false, playlistId = "", rankNumber, topWeeklyCount }: {
    post: any;
    userRole?: string;
    isPlus?: boolean;
    isPromoted?: boolean;
    inline?: boolean;
    showEmbed?: boolean;
    canBeDeleted?: boolean;
    playlistId?: string;
    rankNumber?: number;
    topWeeklyCount?: number;
  }) => {
  const { user } = useAuth();

  const timeAgoString = getTimeAgo(post.created);
  const vote_count = post.active_votes.length;
  let payoutTimeString = getTimeToExpiration(post.created);
  let isExpired = payoutTimeString.includes("Paid") ? true : false;
  const voted = post.active_votes.some(v => v.voter === user);
  const isCommunityMod = ["mod", "admin", "owner"].includes(userRole);

  let thumbnailUrl: string | undefined;
  let videoUrl: string | null = null;
  let videoType: "promoted" | "ipfs" | "youtube" | "clip" | null = null;

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!e.currentTarget.src.includes("no-cover.png")) {
      e.currentTarget.src = "/assets/img/no-cover.png";
    }
  };

  // 1️⃣ find ipfs
  videoUrl = getIPFSVideoUrl(post.body || "", post.video);
  if (videoUrl) {
    videoType = "ipfs";
  }

  // 2️⃣ use post images (Primacy to custom covers/body images)
  if (!thumbnailUrl) {
    let images = getPostImages(post);
    if (images.length > 0) {
      thumbnailUrl = `${images[0]}`;
    }
  }

  // 3️⃣ find yt (Video status only)
  if (!videoUrl && post.video && (post.video.includes("youtu.be") || post.video.includes("youtube"))) {
    videoType = "youtube";
    videoUrl = post.video;
  }

  // 4️⃣ Final fallback
  if (!thumbnailUrl) {
    thumbnailUrl = "/assets/img/no-cover.png";
  }

  const imageLink = `https://steemitimages.com/u/${post.author}/avatar`;
  const title = post.title;
  const isPinned = post.stats?.is_pinned || false;
  const isMuted = post.stats?.gray || false;

  const votesInfo = (
    <span className={`btn-inner--icon ${voted ? "voted" : ""}`}>
      <i className="fas fa-heart" /> {numberWithCommas(vote_count)}
    </span>
  );

  let reputation = (typeof post.author_reputation == "string") ? `(${getReputation(post.author_reputation)})` : `(${post.author_reputation.toFixed(0) || 0})`;

  // 🔹 Render  video embed if showEmbed = true
  const renderVideoEmbed = () => {
    if (videoType === "ipfs" && videoUrl) {
      return (
        <video controls playsInline style={{ maxWidth: "100%", borderRadius: "8px" }}>
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }
    if (videoType === "youtube" && videoUrl) {
      const videoId = videoUrl.split("/").pop()?.split("?")[0];
      return (
        <iframe
          width="100%"
          height="315"
          src={`https://www.youtube.com/embed/${videoId}`}
          frameBorder="0"
          allowFullScreen
        ></iframe>
      );
    }
    return (
      <img src={thumbnailUrl} alt={`Thumbnail for ${post.title}`} className="thumbnail no-video" onError={handleImageError} />
    );
  };
  let urlType = "p";

  if (videoType == "ipfs") {
    try {
      const metadata = post?.json_metadata ? (post.json_metadata) : {};
      if (!metadata?.display_type) {
        // urlType = "clips";
        // videoType = "clip";
      } else {
        if (metadata?.display_type == "clip") {
          urlType = "clips";
          videoType = "clip";
        }
      }
    } catch (err) {
      // Si el JSON no es válido, caemos acá sin romper la app
      // urlType = "clips";
    }
  }

  return (
    <>
      {inline ? (
        <div className='card-stats card item-speem shadow-sm pd-0'>
          <div className="post-video-item-inline card-body p-0 m-0">
            <Link to={`/${urlType}/${post.author}/${post.permlink}`} className="inline-thumbnail">
              <img src={thumbnailUrl} alt={`Thumbnail for ${post.title}`} loading="lazy" onError={handleImageError} />
            </Link>
            <div className="inline-details p-1">
              <Link to={`/p/${post.author}/${post.permlink}`} className="inline-title-link">
                <h4 className="inline-title">{title}</h4>
              </Link>
              <div className="inline-meta">
                <Link to={`/u/${post.author}`} className="no-color inline-title-link">@{post.author}</Link> · {timeAgoString} · {votesInfo}
              </div>
              <RewardsButton post={post}
                className='no-border no-bg-hover-dark p-0  m-0 ' payoutTimeString={payoutTimeString}
                onNewError={(error) => { console.log(error); }} />
            </div>
          </div>
        </div>
      ) : (
        <div className={`card-stats card item-speem ${isPinned ? "pinned" : ""} pd-0 m-0`}>
          <div className="card-body p-0 m-0" style={{ position: "relative" }}>
            <div className="thumbnail-container">
              {showEmbed ? renderVideoEmbed() : (
                <Link to={`/${urlType}/${post.author}/${post.permlink}`} className="thumbnail-wrapper">
                  <img src={thumbnailUrl} alt={`Miniatura de ${post.title}`} className={`thumbnail ${!videoUrl ? "no-video" : ""}`} loading='lazy' onError={handleImageError} />
                  <div className="thumbnail-overlay"></div>
                </Link>
              )}
              {typeof rankNumber === 'number' && (
                <span className={`post-rank-badge post-rank-${rankNumber <= 3 ? rankNumber : 'other'}`}>
                  #{rankNumber} {rankNumber === 1 ? '🥇' : rankNumber === 2 ? '🥈' : rankNumber === 3 ? '🥉' : ''}
                </span>
              )}
              {isPinned && <span className="pin"><i className="fas fa-thumb-tack" /> Pinned</span>}
              {isMuted && <span className="pin"><i className="fas fa-eye-slash" /> Muted</span>}
              <PostBadges postType={videoType ? videoType : "blog"} />
              {(post.video || videoUrl) && <div className="play-icon"> <Link to={`/${urlType}/${post.author}/${post.permlink}`}><img src='/assets/img/play.png' /></Link> </div>}
              <div className="post-rewards">
                <RewardsButton post={post}
                  className='no-pill thumbnail-badge' payoutTimeString={payoutTimeString}
                  onNewError={(error) => { console.log(error); }} />
                <span className={`post-votes  ${voted ? "voted" : "color-white"}`}>
                  <i className="fas fa-heart" /> {numberWithCommas(vote_count)}
                </span>
              </div>
              {(post.video || videoUrl) && <span className="duration"><i className="fas fa-eye" /> {post.views || 0}</span>}
            </div>
            <div className="details p-1">
              <div className='mt-2'>
                <Row className='flex-nowrap m-0'>
                  <div className="img-post-user">
                    <img className="user-post-logo" src={imageLink} alt="User Avatar" />
                  </div>
                  <div className="description-container">
                    <div className="title-row">
                      <Link to={`/${urlType}/${post.author}/${post.permlink}`} className="title-link">
                        <h3 className="title">{title}</h3>
                      </Link>
                    </div>
                    <div className="channel-info">
                      <div className="author-row d-flex align-items-center flex-wrap">
                        {isPromoted && <span className="promoted-label">PROMOTED</span>}
                        <Link to={`/u/${post.author}`} className="channel-link">@{post.author} {reputation}</Link>
                        {(isPlus) && (
                          <div className="verified-badge-container d-inline-flex align-items-center" style={{ margin: "0 2px", cursor: "pointer" }} title='Verified Plus member.'>
                            <VerifiedIcon size={18} className="color-speem" />
                          </div>
                        )}
                        {(topWeeklyCount && topWeeklyCount >= 2) ? (
                          <span className="top-weekly-pill" title={`In daily Top 15 ${topWeeklyCount} times this week`}>
                            <i className="fas fa-fire mr-1" />+{topWeeklyCount} this week
                          </span>
                        ) : null}
                        {(post.author_role && post.author_role !== "guest") && <div className="text-muted d-inline-block" style={{ fontSize: "0.85em", marginLeft: "2px", marginRight: "2px" }}>· {post.author_role}</div>}
                        {post.author_title && <span className="community-label" style={{ whiteSpace: "nowrap" }}>{post.author_title}</span>}
                      </div>
                      <div className="metadata-row">
                        <span className="metadata mr-1">
                          {timeAgoString} <Link to={`/new/${post.category}`} className="channel-link"> In {post.community_title || `#${post.category}`} </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="post-action-column">
                    <PostDropdown
                      isCommunityMod={isCommunityMod}
                      isPinned={isPinned}
                      isMuted={isMuted}
                      communityName={post.category}
                      author={post.author}
                      permlink={post.permlink}
                      urlType={urlType}
                      canBeDeleted={canBeDeleted}
                      playlistId={playlistId}
                      isExpired={isExpired}
                      lightMode={true}
                    />
                  </div>
                </Row>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostVideoItem;
